import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  TemplateRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  inject,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { Store } from '@ngxs/store';
import { FormControl } from '@angular/forms';

import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { SepTableComponent } from '../../../../../shared/components/sep-table/sep-table.component';
import { UserCardComponent } from '../../../../../shared/components/user-card/user-card.component';
import { SelectComponent } from '../../../../../shared/components/select/select.component';
import { Column } from '../../../../../shared/components/sep-table/Column';
import {
  TableBulkAction,
  TableTargetedAction,
  TableToolbarAction,
} from '../../../../../shared/components/sep-table/Action';
import { FilterCategory } from '../../../../../shared/components/sep-filter/sep-filter.component';
import { CurrentUserState } from '../../../../../core/store/current-user/current-user.state';

import { EnrolledStudentTableBaseComponent } from '../../../base/EnrolledStudentTableBaseComponent';
import { LearningPlanEnrolledStudentUIModel } from '../../models/LearningPlanEnrolledStudentUIModel';
import { LearningPlanDataService } from '../../services/learning-plan-data.service';
import { MatIcon } from '@angular/material/icon';
import { FeatureFlagsService } from '../../../../../core/services/feature-flag.service';
import { certificateLanguageSubActions } from '../../../certificate/certificate-language-sub-actions';
import { Status } from '../../models/LearningPlanDTO';

interface SelectOption {
  key: string;
  value: string;
  startDate: string;
  endDate: string;
}

const NO_CERTIFICATE_TOOLTIP_KEY =
  'portal.courses.certificates.noCertificateAvailable';

const CERTIFIED_STATUS: string = Status.COMPLETED;

@Component({
  selector: 'app-learning-plan-enrolled-student-table',
  imports: [
    SepTableComponent,
    UserCardComponent,
    SelectComponent,
    TranslocoDirective,
    TranslocoPipe,
    MatTooltipModule,
    MatIcon,
  ],
  templateUrl: './learning-plan-enrolled-student-table.component.html',
})
export class LearningPlanEnrolledStudentTableComponent
  extends EnrolledStudentTableBaseComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly learningPlanDataService = inject(LearningPlanDataService);
  private readonly store = inject(Store);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  readonly isCertificateExportEnabled: boolean =
    this.featureFlagsService.isFeatureEnabled(
      this.featureFlagsService.certificates,
    );

  @Input() allowEnrollment = false;
  @Input() periodPickerControl?: FormControl<string | null>;
  @Input() periodPickerOptions: SelectOption[] = [];
  @Input() hasDeadline = false;
  @Input() isEnrollmentBlockedByDeadline = false;
  @Output() enrollButtonClicked: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('studentCard', { static: true })
  studentCard!: TemplateRef<{ $implicit: string }>;
  @ViewChild('phaseWithCourse', { static: true })
  phaseWithCourse!: TemplateRef<{ $implicit: string }>;
  @ViewChild('participantStatus', { static: true })
  participantStatus!: TemplateRef<{ $implicit: string }>;
  @ViewChild('participantStatusHeader', { static: true })
  participantStatusHeader!: TemplateRef<{ $implicit: string }>;
  @ViewChild('currentPhaseHeader', { static: true })
  currentPhaseHeader!: TemplateRef<{ $implicit: string }>;
  @ViewChild('progressHeader', { static: true })
  progressHeader!: TemplateRef<{ $implicit: string }>;
  @ViewChild('progress', { static: true })
  progress!: TemplateRef<{ $implicit: string }>;
  @Input() students: LearningPlanEnrolledStudentUIModel[] = [];
  dataSource: LearningPlanEnrolledStudentUIModel[] = [];
  filterCategories: FilterCategory[] = [];
  private readonly destroy$: Subject<void> = new Subject();

  readonly isCurrentPeriod = this.learningPlanDataService.isCurrentPeriod;
  readonly isLoadingStudents = this.learningPlanDataService.isLoadingStudents;
  private readonly currentUserEmail: string | undefined;
  private readonly statusConfig: Record<
    string,
    { colorClass: string; icon: string }
  > = {
    invited: {
      colorClass: 'text-gray-600 bg-grey-lightGrey',
      icon: 'schedule',
    },
    started: {
      colorClass: 'text-gray-600 bg-blue-veryLight',
      icon: 'schedule',
    },
    completed: {
      colorClass: 'text-success bg-success-light',
      icon: 'check_circle',
    },
    overdue: {
      colorClass: 'text-cancel bg-red-50',
      icon: 'warning',
    },
  };

  private readonly defaultStatusConfig = {
    colorClass: 'text-gray-600 bg-grey-lightGrey',
    icon: 'schedule',
  };

  columns: Array<
    Column<LearningPlanEnrolledStudentUIModel, string | { $implicit: string }>
  > = [
    {
      headerName: this.getTranslation(
        'trainings.enrolledStudentTable.headers.nameAndEmail',
      ),
      renderComponent: undefined,
      key: 'username',
    },
    {
      headerName: this.getTranslation(
        'portal.trainings.enrolledStudentTable.headers.participantStatus',
      ),
      renderComponent: undefined,
      key: 'participantStatus',
    },
    {
      headerName: this.getTranslation(
        'trainings.enrolledStudentTable.headers.currentPhase',
      ),
      field: 'currentPhase',
      key: 'currentPhase',
    },
    {
      headerName: this.getTranslation(
        'trainings.enrolledStudentTable.headers.status',
      ),
      field: 'status',
      key: 'status',
    },
  ];

  bulkActions: Array<TableBulkAction<LearningPlanEnrolledStudentUIModel>> = [
    {
      label: 'portal.courses.unenrollment.bulkUnenrollButtonText',
      translate: true,
      icon: 'group_remove',
      checkHide: () => !this.canManageEnrollment(),
      executeAction: (targeted) => {
        this.confirmAndUnenroll(targeted);
      },
    },
    {
      label: 'portal.courses.reminders.bulkSendReminder',
      translate: true,
      icon: 'send',
      checkHide: () => !this.canManageEnrollment(),
      executeAction: (targeted) => {
        this.confirmAndSendReminder(targeted);
      },
    },
    {
      label: 'portal.courses.certificates.bulkExportCertificate',
      translate: true,
      icon: 'workspace_premium',
      checkHide: () => !this.isCertificateExportEnabled,
      checkDisabled: (targeted) => !this.hasAnyCertificate(targeted),
      tooltipLabel: (targeted) =>
        this.hasAnyCertificate(targeted) ? '' : NO_CERTIFICATE_TOOLTIP_KEY,
      subActions: certificateLanguageSubActions((targeted, language) => {
        this.learningPlanDataService.exportCertificateBundle(
          targeted.filter((student) => this.hasCertificate(student)),
          language,
        );
      }),
    },
  ];

  rowActions: Array<TableTargetedAction<LearningPlanEnrolledStudentUIModel>> = [
    {
      label: 'portal.courses.unenrollment.unenrollButtonText',
      translate: true,
      icon: 'person_remove',
      checkHide: () => !this.canManageEnrollment(),
      executeAction: (student) => {
        this.confirmAndUnenroll([student]);
      },
    },
    {
      label: 'portal.courses.reminders.sendReminder',
      translate: true,
      icon: 'send',
      checkHide: () => !this.canManageEnrollment(),
      executeAction: (student) => {
        this.confirmAndSendReminder([student]);
      },
    },
    {
      label: 'portal.courses.certificates.exportCertificate',
      translate: true,
      icon: 'workspace_premium',
      checkHide: () => !this.isCertificateExportEnabled,
      checkDisabled: (student) => !this.hasCertificate(student),
      tooltipLabel: (student) =>
        this.hasCertificate(student) ? '' : NO_CERTIFICATE_TOOLTIP_KEY,
      subActions: certificateLanguageSubActions((student, language) => {
        this.learningPlanDataService.exportCertificate(student, language);
      }),
    },
  ];

  toolbarExportActions: TableToolbarAction[] = this.isCertificateExportEnabled
    ? [
        {
          label: 'portal.users.table.actions.exportAllCertificates',
          translate: true,
          icon: 'workspace_premium',
          checkDisabled: () => !this.hasAnyCertificate(this.students),
          tooltipLabel: () =>
            this.hasAnyCertificate(this.students)
              ? ''
              : NO_CERTIFICATE_TOOLTIP_KEY,
          subActions: certificateLanguageSubActions<void>(
            (target, language) => {
              this.learningPlanDataService.exportCertificateBundle(
                this.students.filter((student) => this.hasCertificate(student)),
                language,
              );
            },
          ),
        },
        {
          label: 'portal.courses.certificates.exportStatistics',
          translate: true,
          icon: 'format_list_bulleted',
          executeAction: () => {
            this.exportTableContentsToCsv();
          },
        },
      ]
    : [];

  readonly bulkActionsNote = this.isCertificateExportEnabled
    ? this.getTranslation('courses.certificates.appliesToSelectedUsers')
    : undefined;

  constructor() {
    super();
    const userDetails = this.store.selectSnapshot(CurrentUserState.userDetails);
    this.currentUserEmail = userDetails.email;
  }

  ngOnChanges() {
    this.dataSource = this.translateProperties(this.students);
    this.initializeLearningPlanStatusFilter();
  }

  ngAfterViewInit(): void {
    this.columns[0].renderComponent = this.studentCard;
    this.columns[1].renderComponent = this.participantStatus;
    this.columns[1].headerRenderComponent = this.participantStatusHeader;
    this.columns[2].renderComponent = this.phaseWithCourse;
    this.columns[2].headerRenderComponent = this.currentPhaseHeader;
    this.columns[3].headerRenderComponent = this.progressHeader;
    this.columns[3].renderComponent = this.progress;
    // Remove field to ensure renderComponent is used instead
    this.columns[2].field = undefined;
    this.columns[3].field = undefined;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private confirmAndUnenroll(
    students: LearningPlanEnrolledStudentUIModel[],
  ): void {
    this.showUnenrollmentConfirmationDialog(students, () => {
      this.unenrollStudents(students);
    });
  }

  private confirmAndSendReminder(
    students: LearningPlanEnrolledStudentUIModel[],
  ): void {
    this.showSendReminderConfirmationDialog(students, () => {
      this.sendReminder(students);
    });
  }

  private unenrollStudents(
    students: LearningPlanEnrolledStudentUIModel[],
  ): void {
    this.learningPlanDataService.unenrollStudents(students);
  }

  private sendReminder(students: LearningPlanEnrolledStudentUIModel[]) {
    this.learningPlanDataService.sendReminder(students);
  }

  exportTableContentsToCsv(): void {
    const startDate =
      this.learningPlanDataService.selectedPeriodPickerControlOption()
        ?.startDate;
    const endDate =
      this.learningPlanDataService.selectedPeriodPickerControlOption()?.endDate;
    this.analyticsService.track('statistic_exported', {
      periodStartDate: startDate ?? '',
      periodEndDate: endDate ?? '',
    });
    this.learningPlanDataService.exportStudentInfoToCsv();
  }

  override showCourseEnrollmentDialog(): void {
    this.enrollButtonClicked.emit();
    super.showCourseEnrollmentDialog();
  }

  isCurrentUser(student: LearningPlanEnrolledStudentUIModel): boolean {
    return this.currentUserEmail === student.email;
  }

  canManageEnrollment(): boolean {
    return this.hasDeadline || this.isCurrentPeriod();
  }

  hasCertificate(student: LearningPlanEnrolledStudentUIModel): boolean {
    return student.status === CERTIFIED_STATUS;
  }

  private hasAnyCertificate(
    students: LearningPlanEnrolledStudentUIModel[],
  ): boolean {
    return students.some((student) => this.hasCertificate(student));
  }

  private initializeLearningPlanStatusFilter(): void {
    if (!this.dataSource.length) {
      this.filterCategories = [];
      return;
    }

    // Extract unique values from dataSource (which contains translated data), NOT from students (raw data).
    // This ensures filter values match exactly what's in the table for proper filtering.
    //
    // PARTICIPANT STATUS: Service stores "active"/"inactive" raw values, dataSource keeps them unchanged,
    // so filter value is "active"/"inactive" matching dataSource directly.
    //
    // CURRENT PHASE: Backend sends raw "YearOne", translateProperties converts to "Year 1", dataSource stores "Year 1",
    // so filter value must be "Year 1" translated to match dataSource.
    //
    // PROGRESS STATUS: Backend sends raw "NotStarted", translateProperties converts to "Not started", dataSource stores "Not started",
    // so filter value must be "Not started" translated to match dataSource.
    //
    const uniqueParticipantStatuses = Array.from(
      new Set(this.dataSource.map((s) => s.participantStatus).filter(Boolean)),
    );
    const uniquePhases = Array.from(
      new Set(this.dataSource.map((s) => s.currentPhase)),
    );
    const uniqueStatuses = Array.from(
      new Set(this.dataSource.map((s) => s.status)),
    );

    const participantStatusOptions =
      uniqueParticipantStatuses.length > 0
        ? uniqueParticipantStatuses.map((status) => ({
            label: this.getTranslation(
              'trainings.enrolledStudentTable.participantStatus.' + status,
            ),
            value: status,
            selected: true,
          }))
        : [];

    const phaseOptions = uniquePhases.map((phase) => ({
      label: this.getTranslation(
        'trainings.enrolledStudentTable.currentPhase.' + phase,
      ),
      value: phase,
      selected: true,
    }));

    this.filterCategories = [];

    if (participantStatusOptions.length > 0) {
      this.filterCategories.push({
        label: this.getTranslation(
          'trainings.enrolledStudentTable.headers.participantStatus',
        ),
        value: 'participantStatus',
        options: participantStatusOptions,
      });
    }

    if (!this.hasDeadline) {
      this.filterCategories.push({
        label: this.getTranslation(
          'trainings.enrolledStudentTable.headers.currentPhase',
        ),
        value: 'currentPhase',
        options: phaseOptions,
      });
    }

    this.filterCategories.push({
      label: this.getTranslation(
        'trainings.enrolledStudentTable.headers.status',
      ),
      value: 'status',
      options: uniqueStatuses.map((status) => ({
        label: this.getTranslation(
          'courses.analytics.courseProgress.' + status,
        ),
        value: status,
        selected: true,
      })),
    });
  }

  getStatusColorClass(status: string): string {
    return (
      this.statusConfig[status.toLowerCase().trim()] ?? this.defaultStatusConfig
    ).colorClass;
  }

  getStatusIcon(status: string): string {
    return (
      this.statusConfig[status.toLowerCase().trim()] ?? this.defaultStatusConfig
    ).icon;
  }

  translateProperties(
    students: LearningPlanEnrolledStudentUIModel[],
  ): LearningPlanEnrolledStudentUIModel[] {
    return students.map((student) => ({
      ...student,
      currentPhase: this.getTranslation(
        'trainings.enrolledStudentTable.currentPhase.' + student.currentPhase,
      ),
      participantStatus: student.participantStatus,
    }));
  }
}
